import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { fullErrorText } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SlideOver, SlideOverContent } from '@/components/ui/slide-over';
import { LoaderIcon, PencilIcon, PlusIcon, TrashIcon } from '@/components/icons';
import { Skill, SkillStatus } from '@/proto/agentplatform/v1/skill_pb';
import { cortexApi } from '@/api/cortex.api';
import { formatSkillTimestamp } from './skill.utils';

type FormState = {
  name: string;
  description: string;
  tagsText: string;
  content: string;
  status: SkillStatus;
};

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  tagsText: '',
  content: '',
  status: SkillStatus.AVAILABLE,
};

// Keep these rules in sync with admin-frontend until both apps share a package.
const SKILL_NAME_MIN_LENGTH = 3;
const SKILL_NAME_MAX_LENGTH = 64;
const SKILL_NAME_PATTERN = new RegExp(
  `^[A-Za-z0-9_-]{${SKILL_NAME_MIN_LENGTH},${SKILL_NAME_MAX_LENGTH}}$`
);
const SKILL_NAME_ERROR = `Skill 名称仅支持 ${SKILL_NAME_MIN_LENGTH}-${SKILL_NAME_MAX_LENGTH} 位英文、数字、连字符(-) 和下划线(_)`;
const isSkillNameOverLength = (name: string) => name.trim().length > SKILL_NAME_MAX_LENGTH;

function validateSkillName(name: string): string {
  const trimmedName = name.trim();
  if (!trimmedName) return '请输入 Skill 名称';
  if (isSkillNameOverLength(name)) {
    return `Skill 名称不能超过 ${SKILL_NAME_MAX_LENGTH} 个字符（当前 ${trimmedName.length} 个）`;
  }
  return SKILL_NAME_PATTERN.test(trimmedName) ? '' : SKILL_NAME_ERROR;
}

const statusLabel: Record<SkillStatus, string> = {
  [SkillStatus.UNAVAILABLE]: '不可用',
  [SkillStatus.AVAILABLE]: '可用',
  [SkillStatus.UPLOADING]: '上传中',
};

const statusBadgeClass: Record<SkillStatus, string> = {
  [SkillStatus.UNAVAILABLE]: 'bg-destructive/10 text-destructive border-destructive/20',
  [SkillStatus.AVAILABLE]: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  [SkillStatus.UPLOADING]: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
};

export default function SkillsManagementPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [editing, setEditing] = useState<Skill | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [nameError, setNameError] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['skills-management', page, pageSize, keyword],
    queryFn: () =>
      cortexApi.listSkills({
        page,
        pageSize,
        keyword: keyword.trim(),
        withVersions: false,
      }),
  });

  const skills = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const availableCount = skills.filter((s) => s.status === SkillStatus.AVAILABLE).length;
  const uploadingCount = skills.filter((s) => s.status === SkillStatus.UPLOADING).length;

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      cortexApi.createSkill({
        request: {
          name,
          description: form.description.trim(),
          status: Number(form.status),
          tags: form.tagsText
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean),
          content: form.content,
        },
      }),
    onSuccess: () => {
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      queryClient.invalidateQueries({ queryKey: ['skills-management'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (name: string) =>
      editing
        ? cortexApi.updateSkill(editing.id, {
            request: {
              name,
              description: form.description.trim(),
              status: Number(form.status),
            },
          })
        : Promise.resolve(null),
    onSuccess: () => {
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      queryClient.invalidateQueries({ queryKey: ['skills-management'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => cortexApi.deleteSkill(id),
    onSuccess: () => {
      setDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ['skills-management'] });
    },
  });

  const importZipMutation = useMutation({
    mutationFn: (file: File) => cortexApi.importSkillZip(file),
    onSuccess: (response, file) => {
      queryClient.invalidateQueries({ queryKey: ['skills-management'] });
      const name = response.data?.name?.trim() || file.name;
      toast.success(name ? `Skill「${name}」导入成功` : 'Skill 导入成功');
    },
    onError: (error) => toast.error(fullErrorText(error)),
  });

  const isNameUnchanged = (name: string) =>
    Boolean(editing && (editing.name === name || editing.name === name.trim()));
  const submitDisabled = useMemo(() => {
    const trimmedName = form.name.trim();
    if (!trimmedName) return true;
    if (editing && (editing.name === form.name || editing.name === trimmedName)) return false;
    return !SKILL_NAME_PATTERN.test(trimmedName);
  }, [form.name, editing]);
  const validateFormName = (name: string) => (isNameUnchanged(name) ? '' : validateSkillName(name));

  const onSubmit = () => {
    const trimmedName = form.name.trim();
    const nameUnchanged = isNameUnchanged(form.name);
    const error = validateFormName(form.name);
    setNameError(error);
    if (error) return;

    if (editing) {
      updateMutation.mutate(nameUnchanged ? editing.name : trimmedName);
      return;
    }
    createMutation.mutate(trimmedName);
  };

  const startEdit = (item: Skill) => {
    setNameError('');
    setEditing(item);
    setForm({
      name: item.name,
      description: item.description,
      tagsText: item.tags.join(','),
      content: '',
      status: item.status,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setNameError('');
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const openCreate = () => {
    setNameError('');
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const triggerImportZip = () => {
    fileInputRef.current?.click();
  };

  const onImportZip = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      await importZipMutation.mutateAsync(file);
    } catch {
      // The mutation's onError callback displays the concrete failure reason.
    } finally {
      e.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-muted/20">
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Skills 管理</h1>
            <p className="text-sm text-muted-foreground mt-1">管理 Skills 元信息、版本与附件。</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => refetch()}>
              刷新
            </Button>
            <Button
              variant="outline"
              onClick={triggerImportZip}
              disabled={importZipMutation.isPending}
            >
              {importZipMutation.isPending && <LoaderIcon className="h-4 w-4 mr-1" />}
              {importZipMutation.isPending ? '导入中...' : '导入 zip（新建 Skill）'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={onImportZip}
            />
            <Button onClick={openCreate}>
              <PlusIcon className="h-4 w-4 mr-1" />
              新建 Skill
            </Button>
          </div>
        </div>

        <div className="bg-background rounded-xl border shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              placeholder="关键字"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
            />
            <div />
          </div>

          {isLoading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-8 text-sm text-destructive">加载失败，请重试。</div>
          ) : (
            <>
              <div className="px-5 py-3 border-b grid grid-cols-3 gap-3 bg-muted/20">
                <div className="rounded-md border bg-background px-3 py-2">
                  <div className="text-xs text-muted-foreground">当前页条数</div>
                  <div className="text-lg font-semibold">{skills.length}</div>
                </div>
                <div className="rounded-md border bg-background px-3 py-2">
                  <div className="text-xs text-muted-foreground">可用 Skill</div>
                  <div className="text-lg font-semibold text-emerald-600">{availableCount}</div>
                </div>
                <div className="rounded-md border bg-background px-3 py-2">
                  <div className="text-xs text-muted-foreground">上传中 Skill</div>
                  <div className="text-lg font-semibold text-amber-600">{uploadingCount}</div>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>名称</TableHead>
                    <TableHead>描述</TableHead>
                    <TableHead>创建时间</TableHead>
                    <TableHead>更新时间</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>标签</TableHead>
                    <TableHead className="w-48">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {skills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                        暂无 Skill
                      </TableCell>
                    </TableRow>
                  ) : (
                    skills.map((item) => (
                      <TableRow key={item.id} className="hover:bg-muted/20">
                        <TableCell>
                          <button
                            className="block max-w-80 truncate font-medium text-primary hover:underline text-left"
                            title={item.name}
                            onClick={() => navigate(`/plugins/skills/management/${item.id}`)}
                          >
                            {item.name}
                          </button>
                          <div className="text-xs text-muted-foreground">ID: {item.id}</div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {item.description || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatSkillTimestamp(item.createdAt)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatSkillTimestamp(item.updatedAt)}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadgeClass[item.status]}>
                            {statusLabel[item.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(item.tags || []).length > 0 ? (
                              item.tags.slice(0, 4).map((tag) => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                            {(item.tags || []).length > 4 && (
                              <Badge variant="outline" className="text-xs">
                                +{item.tags.length - 4}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/plugins/skills/management/${item.id}`)}
                            >
                              详情/版本
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`编辑 ${item.name}`}
                              onClick={() => startEdit(item)}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`删除 ${item.name}`}
                              className="text-muted-foreground hover:text-destructive"
                              onClick={() => setDeleteId(item.id)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}

          <div className="px-5 py-3 border-t flex items-center justify-between text-sm">
            <span className="text-muted-foreground">共 {total} 条</span>
            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                上一页
              </Button>
              <span>
                {page}/{totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                下一页
              </Button>
            </div>
          </div>
        </div>
      </div>

      <SlideOver
        open={showForm}
        onOpenChange={(open) => {
          if (!open) {
            resetForm();
          } else {
            setShowForm(true);
          }
        }}
        title={editing ? '编辑 Skill' : '新建 Skill'}
        description={editing ? '更新 Skill 基础信息。' : '创建 Skill，并可附带初始 SKILL.md 内容。'}
        actions={
          <Button
            onClick={onSubmit}
            disabled={submitDisabled || createMutation.isPending || updateMutation.isPending}
          >
            {(createMutation.isPending || updateMutation.isPending) && (
              <LoaderIcon className="h-4 w-4 mr-1" />
            )}
            {editing ? '保存' : '创建'}
          </Button>
        }
      >
        <SlideOverContent>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">名称 *</p>
            <Input
              placeholder="3-64 位英文/数字/-/_"
              value={form.name}
              aria-label="Skill 名称"
              aria-invalid={Boolean(nameError)}
              aria-describedby={nameError ? 'skill-name-error' : undefined}
              onBlur={() => setNameError(validateFormName(form.name))}
              onChange={(e) => {
                const name = e.target.value;
                setForm((s) => ({ ...s, name }));
                setNameError((currentError) => {
                  const error = validateFormName(name);
                  return isSkillNameOverLength(name) || currentError ? error : '';
                });
              }}
            />
            {nameError && (
              <p id="skill-name-error" className="text-sm text-destructive" role="alert">
                {nameError}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">描述</p>
            <textarea
              placeholder="可选，描述此 Skill 的用途"
              value={form.description}
              onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
              className="w-full min-h-20 rounded-md border border-input px-3 py-2 text-sm bg-background"
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">状态</p>
            <select
              value={form.status}
              onChange={(e) =>
                setForm((s) => ({ ...s, status: Number(e.target.value) as SkillStatus }))
              }
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value={SkillStatus.AVAILABLE}>可用</option>
              <option value={SkillStatus.UNAVAILABLE}>不可用</option>
              <option value={SkillStatus.UPLOADING}>上传中</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">标签（逗号分隔）</p>
            <Input
              placeholder="如 nlp, agent, tool"
              value={form.tagsText}
              onChange={(e) => setForm((s) => ({ ...s, tagsText: e.target.value }))}
            />
          </div>
          {!editing && (
            <div className="space-y-1.5">
              <p className="text-sm font-medium">SKILL.md 内容</p>
              <textarea
                placeholder="可选，创建时写入初始 SKILL.md"
                value={form.content}
                onChange={(e) => setForm((s) => ({ ...s, content: e.target.value }))}
                className="w-full min-h-40 rounded-md border border-input px-3 py-2 text-sm font-mono bg-background"
              />
            </div>
          )}
        </SlideOverContent>
      </SlideOver>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>删除 Skill 后不可恢复，确认继续？</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <LoaderIcon className="h-4 w-4 mr-1" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
